import React, { useState } from "react";
import { makeStyles, useTheme } from "@material-ui/core/styles";
import {
  Paper,
  Typography,
  Box,
  Chip,
  Tooltip,
  Button,
  CircularProgress,
  ButtonGroup
} from "@material-ui/core";
import {
  TrendingUp,
  FilterList,
  AttachMoney,
  AccessTime,
  ArrowForward,
  CheckCircle,
  Clear,
  ViewStream,
  ViewColumn,
  InfoOutlined
} from "@material-ui/icons";
import { i18n } from "../../translate/i18n";
import StandardTabs from "../StandardTabs";

const useStyles = makeStyles((theme) => ({
  container: {
    padding: theme.spacing(2.5),
    marginBottom: theme.spacing(3),
    borderRadius: 12,
    backgroundColor: theme.palette.type === "dark" 
      ? "rgba(22, 27, 34, 0.75)" 
      : "#FFFFFF",
  },
  header: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    flexWrap: "wrap",
    gap: theme.spacing(2),
    marginBottom: theme.spacing(2.5),
    paddingBottom: theme.spacing(1.5),
    borderBottom: theme.palette.type === "dark" 
      ? "1px solid rgba(255, 255, 255, 0.06)" 
      : "1px solid rgba(0, 0, 0, 0.08)",
  },
  headerTitle: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1.5),
    fontSize: "1.1rem",
    fontWeight: 700,
    color: theme.palette.type === "dark" ? "#FFFFFF" : "#0F172A",
    letterSpacing: "0.5px",
  },
  titleIcon: {
    color: theme.palette.primary.main,
    fontSize: 26,
  },
  headerStats: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1.5),
    flexWrap: "wrap",
  },
  summaryChip: {
    backgroundColor: theme.palette.type === "dark" ? "rgba(255, 255, 255, 0.04)" : "rgba(0, 0, 0, 0.04)",
    border: theme.palette.type === "dark" ? "1px solid rgba(255, 255, 255, 0.1)" : "1px solid rgba(0, 0, 0, 0.1)",
    color: theme.palette.type === "dark" ? "#FFFFFF" : "#1E293B",
    fontWeight: 600,
    fontSize: "0.8rem",
    height: 32,
    "& .MuiChip-icon": {
      color: theme.palette.primary.main,
    },
  },
  viewToggleGroup: {
    backgroundColor: theme.palette.type === "dark" ? "rgba(255, 255, 255, 0.04)" : "rgba(0, 0, 0, 0.03)",
    borderRadius: 8,
    border: theme.palette.type === "dark" ? "1px solid rgba(255, 255, 255, 0.1)" : "1px solid rgba(0, 0, 0, 0.1)",
    "& .MuiButton-root": {
      color: theme.palette.type === "dark" ? "rgba(255, 255, 255, 0.6)" : "#475569",
      borderColor: theme.palette.type === "dark" ? "rgba(255, 255, 255, 0.1)" : "rgba(0, 0, 0, 0.08)",
      textTransform: "none",
      fontSize: "0.75rem",
      fontWeight: 600,
      padding: "4px 10px",
    },
  },
  activeViewBtn: {
    backgroundColor: `${theme.palette.primary.main}26 !important`,
    color: `${theme.palette.primary.main} !important`,
    borderColor: `${theme.palette.primary.main}66 !important`,
  },
  // Contenedor del Gráfico de Embudo SVG Cónico
  svgFunnelWrapper: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    width: "100%",
    padding: theme.spacing(1.5, 0),
    overflowX: "auto",
  },
  svgFunnel: {
    maxWidth: 960,
    width: "100%",
    filter: theme.palette.type === "dark" 
      ? "drop-shadow(0px 8px 16px rgba(0, 0, 0, 0.4))" 
      : "drop-shadow(0px 4px 10px rgba(0, 0, 0, 0.08))",
  },
  trapezoidSegment: {
    cursor: "pointer",
    outline: "none",
    userSelect: "none",
    "& text": {
      stroke: "none !important",
      userSelect: "none",
    },
  },
  trapezoidPolygon: {
    transition: "all 0.25s cubic-bezier(0.4, 0, 0.2, 1)",
    "&:hover": {
      filter: `brightness(1.25) drop-shadow(0 0 8px ${theme.palette.primary.main}66)`,
    },
  },
  trapezoidPolygonActive: {
    stroke: `${theme.palette.primary.main} !important`,
    strokeWidth: "3.5 !important",
    filter: `drop-shadow(0 0 12px ${theme.palette.primary.main}D9) !important`,
  },
  // Grid de Tarjetas Horizontales
  funnelGrid: {
    display: "flex",
    alignItems: "stretch",
    gap: theme.spacing(1.5),
    overflowX: "auto",
    paddingBottom: theme.spacing(1.5),
    marginTop: theme.spacing(2),
    "&::-webkit-scrollbar": {
      height: 6,
    },
    "&::-webkit-scrollbar-thumb": {
      backgroundColor: theme.palette.type === "dark" ? "rgba(255, 255, 255, 0.15)" : "rgba(0, 0, 0, 0.15)",
      borderRadius: 3,
    },
  },
  stageCard: {
    flex: "1 1 210px",
    minWidth: 200,
    maxWidth: 280,
    minHeight: 165,
    padding: theme.spacing(2),
    borderRadius: 14,
    background: theme.palette.type === "dark" ? "rgba(255, 255, 255, 0.02)" : "#FAFAFA",
    border: theme.palette.type === "dark" ? "1px solid rgba(255, 255, 255, 0.08)" : "1px solid rgba(0, 0, 0, 0.08)",
    boxShadow: theme.palette.type === "dark" ? "none" : "0 1px 4px rgba(0, 0, 0, 0.04)",
    cursor: "pointer",
    position: "relative",
    display: "flex",
    flexDirection: "column",
    justifyContent: "space-between",
    transition: "all 0.25s cubic-bezier(0.4, 0, 0.2, 1)",
    "&:hover": {
      transform: "translateY(-3px)",
      background: theme.palette.type === "dark" ? "rgba(255, 255, 255, 0.04)" : "#F1F5F9",
      borderColor: `${theme.palette.primary.main}66`,
      boxShadow: `0 6px 20px ${theme.palette.primary.main}26`,
    },
  },
  activeStageCard: {
    borderColor: `${theme.palette.primary.main} !important`,
    background: `${theme.palette.primary.main}10 !important`,
    boxShadow: `0 0 20px ${theme.palette.primary.main}40 !important`,
  },
  stageTopBar: {
    height: 4,
    borderRadius: 2,
    marginBottom: theme.spacing(1.5),
  },
  stageTitle: {
    fontSize: "0.9rem",
    fontWeight: 700,
    color: theme.palette.type === "dark" ? "#FFFFFF" : "#0F172A",
    marginBottom: theme.spacing(1),
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  },
  countText: {
    fontSize: "1.6rem",
    fontWeight: 800,
    color: theme.palette.type === "dark" ? "#FFFFFF" : "#0F172A",
    lineHeight: 1.1,
  },
  leadsLabel: {
    fontSize: "0.75rem",
    color: theme.palette.type === "dark" ? "rgba(255, 255, 255, 0.6)" : "#64748B",
    fontWeight: 500,
    marginLeft: 4,
  },
  valueText: {
    fontSize: "0.85rem",
    fontWeight: 700,
    color: theme.palette.type === "dark" ? "#4CAF50" : "#16A34A",
    display: "flex",
    alignItems: "center",
    gap: 4,
    marginTop: theme.spacing(0.5),
  },
  leadTimeBadge: {
    marginTop: theme.spacing(1.5),
    padding: "4px 8px",
    borderRadius: 8,
    background: theme.palette.type === "dark" ? "rgba(255, 255, 255, 0.04)" : "rgba(0, 0, 0, 0.03)",
    border: theme.palette.type === "dark" ? "1px solid rgba(255, 255, 255, 0.08)" : "1px solid rgba(0, 0, 0, 0.08)",
    display: "flex",
    alignItems: "center",
    gap: 6,
    fontSize: "0.75rem",
    color: theme.palette.type === "dark" ? "rgba(255, 255, 255, 0.75)" : "#334155",
  },
  leadTimeIcon: {
    fontSize: 14,
    color: theme.palette.type === "dark" ? "#FFB300" : "#D97706",
  },
  connectorWrapper: {
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    alignItems: "center",
    minWidth: 46,
    padding: "0 4px",
  },
  connectorChip: {
    backgroundColor: `${theme.palette.primary.main}14`,
    border: `1px solid ${theme.palette.primary.main}33`,
    color: theme.palette.primary.main,
    fontWeight: 700,
    fontSize: "0.7rem",
    height: 22,
    padding: "0 4px",
    marginBottom: 4,
  },
  connectorArrow: {
    color: theme.palette.type === "dark" ? "rgba(255, 255, 255, 0.3)" : "rgba(0, 0, 0, 0.3)",
    fontSize: 20,
  },
  dropoffLabel: {
    fontSize: "0.65rem",
    color: "#FF5252",
    fontWeight: 600,
    marginTop: 2,
  },
  clearBtn: {
    color: theme.palette.primary.main,
    borderColor: `${theme.palette.primary.main}4D`,
    textTransform: "none",
    fontWeight: 600,
    fontSize: "0.75rem",
    "&:hover": {
      borderColor: theme.palette.primary.main,
      background: `${theme.palette.primary.main}10`,
    },
  },
  promptContainer: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(2),
    padding: theme.spacing(2.5),
    background: `${theme.palette.primary.main}10`,
    borderRadius: 12,
    border: `1px dashed ${theme.palette.primary.main}4D`,
    color: theme.palette.type === "dark" ? "#FFFFFF" : "#0F172A",
  },
}));

const FunnelChart = ({
  selectedBoard,
  funnelData,
  loading = false,
  selectedLane = "",
  onSelectStage,
  currency = "$"
}) => {
  const classes = useStyles();
  const theme = useTheme();
  const isLight = theme.palette.type === "light";
  const [viewMode, setViewMode] = useState("funnel"); // 'funnel' | 'pipeline'

  if (loading) {
    return (
      <Paper className={classes.container} variant="outlined">
        <Box display="flex" justifyContent="center" alignItems="center" p={4}>
          <CircularProgress size={32} style={{ color: theme.palette.primary.main }} />
          <Typography variant="body2" style={{ marginLeft: 16, color: "#AAA" }}>
            {i18n.t("reports.funnel.loading", "Calculando métricas de embudo y velocidad de lead time...")}
          </Typography>
        </Box>
      </Paper>
    );
  }

  // Si no hay tablero seleccionado, mostrar banner informativo con instrucciones
  if (!selectedBoard) {
    return (
      <Paper className={classes.container} variant="outlined">
        <div className={classes.promptContainer}>
          <InfoOutlined style={{ fontSize: 32, color: theme.palette.primary.main }} />
          <div>
            <Typography variant="subtitle1" style={{ fontWeight: 700, color: isLight ? "#0F172A" : "#FFFFFF" }}>
              {i18n.t("reports.funnel.title", "Embudo Visual de Ventas y Tasa de Conversión")}
            </Typography>
            <Typography variant="body2" style={{ color: isLight ? "#475569" : "rgba(255, 255, 255, 0.7)" }}>
              {i18n.t(
                "reports.funnel.selectBoardPrompt",
                "Selecciona un Embudo (Tablero) en los filtros de abajo para visualizar el gráfico cónico de conversión y trazabilidad."
              )}
            </Typography>
          </div>
        </div>
      </Paper>
    );
  }

  if (!funnelData || !funnelData.stages || funnelData.stages.length === 0) {
    return null;
  }

  const { stages, boardName, totalLeads, totalValue, overallConversion } = funnelData;

  // Parámetros de geometría para el Gráfico SVG en forma de Embudo Cónico
  const numStages = stages.length;
  const svgWidth = 960;
  const tierHeight = 58;
  const tierGap = 16;
  const svgHeight = numStages * (tierHeight + tierGap) + 16;
  const maxWidth = 900;
  const minWidth = Math.max(560, maxWidth - numStages * 85);
  const widthStep = (maxWidth - minWidth) / numStages;

  return (
    <Paper className={classes.container} variant="outlined">
      {/* Cabecera del Embudo */}
      <div className={classes.header}>
        <div className={classes.headerTitle}>
          <FilterList className={classes.titleIcon} />
          <span>
            {i18n.t("reports.funnel.title", "Embudo Visual de Ventas y Tasa de Conversión")}
            {boardName ? ` — ${boardName}` : ""}
          </span>
        </div>

        <div className={classes.headerStats}>
          <Chip
            icon={<CheckCircle style={{ fontSize: 16 }} />}
            label={`${totalLeads} ${i18n.t("reports.funnel.totalLeads", "Leads en Embudo")}`}
            className={classes.summaryChip}
          />
          <Chip
            icon={<AttachMoney style={{ fontSize: 16, color: "#4CAF50" }} />}
            label={`${currency} ${totalValue.toFixed(2)}`}
            className={classes.summaryChip}
          />
          <Chip
            icon={<TrendingUp style={{ fontSize: 16, color: "#FFB300" }} />}
            label={`${overallConversion}% ${i18n.t("reports.funnel.overallConversion", "Conversión Global")}`}
            className={classes.summaryChip}
          />

          {selectedLane && (
            <Button
              size="small"
              variant="outlined"
              className={classes.clearBtn}
              startIcon={<Clear style={{ fontSize: 14 }} />}
              onClick={() => onSelectStage("")}
            >
              {i18n.t("reports.funnel.viewAll", "Ver todo el embudo")}
            </Button>
          )}
        </div>
      </div>

      {/* Tabs de Selección de Modo (Embudo Cónico vs Pipeline) */}
      <div style={{ marginBottom: 16, display: "flex", justifyContent: "flex-start" }}>
        <StandardTabs
          value={viewMode}
          onChange={(e, val) => setViewMode(val)}
          items={[
            {
              value: "funnel",
              label: i18n.t("reports.funnel.viewModeFunnel", "Vista Embudo Cónico"),
              icon: <ViewStream style={{ fontSize: 17 }} />,
            },
            {
              value: "pipeline",
              label: i18n.t("reports.funnel.viewModePipeline", "Vista Pipeline"),
              icon: <ViewColumn style={{ fontSize: 17 }} />,
            },
          ]}
        />
      </div>

      {/* 1. MODO: GRÁFICO VISUAL EN FORMA DE EMBUDO CÓNICO (SVG) */}
      {viewMode === "funnel" && (
        <div className={classes.svgFunnelWrapper}>
          <svg
            viewBox={`0 0 ${svgWidth} ${svgHeight}`}
            className={classes.svgFunnel}
            xmlns="http://www.w3.org/2000/svg"
          >
            <defs>
              {stages.map((stage) => {
                const isSelected = String(selectedLane) === String(stage.id);
                return (
                  <linearGradient
                    key={`grad-${stage.id}`}
                    id={`funnel-grad-${stage.id}`}
                    x1="0%"
                    y1="0%"
                    x2="100%"
                    y2="100%"
                  >
                    <stop
                      offset="0%"
                      stopColor={stage.color || "#00E5FF"}
                      stopOpacity={isLight ? (isSelected ? "0.38" : "0.22") : (isSelected ? "0.60" : "0.45")}
                    />
                    <stop
                      offset="100%"
                      stopColor={stage.color || "#00E5FF"}
                      stopOpacity={isLight ? (isSelected ? "0.16" : "0.08") : (isSelected ? "0.22" : "0.12")}
                    />
                  </linearGradient>
                );
              })}
            </defs>

            {stages.map((stage, idx) => {
              const isSelected = String(selectedLane) === String(stage.id);
              const y0 = idx * (tierHeight + tierGap) + 5;
              const y1 = y0 + tierHeight;

              const wTop = maxWidth - idx * widthStep;
              const wBot = maxWidth - (idx + 1) * widthStep;

              const xTopLeft = (svgWidth - wTop) / 2;
              const xTopRight = svgWidth - xTopLeft;
              const xBotLeft = (svgWidth - wBot) / 2;
              const xBotRight = svgWidth - xBotLeft;

              const points = `${xTopLeft},${y0} ${xTopRight},${y0} ${xBotRight},${y1} ${xBotLeft},${y1}`;
              const centerY = y0 + tierHeight / 2;

              // Paredes diagonales reales a la altura media centerY
              const xLeftMid = (xTopLeft + xBotLeft) / 2;
              const xRightMid = (xTopRight + xBotRight) / 2;
              const innerPad = 32; // Margen de seguridad estricto anticolisión
              const badgeWidth = 145;
              const badgeHeight = 26;
              const badgeX = xRightMid - innerPad - badgeWidth;
              const badgeY = centerY - badgeHeight / 2;

              return (
                <g
                  key={stage.id}
                  className={classes.trapezoidSegment}
                  onClick={() => onSelectStage(stage.id)}
                >
                  {/* Trapecio Cónico de la Etapa */}
                  <polygon
                    points={points}
                    fill={`url(#funnel-grad-${stage.id})`}
                    stroke={isSelected ? "#00E5FF" : stage.color || "#00E5FF"}
                    strokeWidth={isSelected ? "3.5" : "1.5"}
                    className={`${classes.trapezoidPolygon} ${isSelected ? classes.trapezoidPolygonActive : ""}`}
                    rx={6}
                  />

                  {/* Icono de color */}
                  <circle
                    cx={xLeftMid + innerPad + 8}
                    cy={centerY}
                    r={6.5}
                    fill={stage.color || "#00E5FF"}
                    stroke="none"
                    style={{ stroke: "none", pointerEvents: "none" }}
                  />

                  {/* Nombre de la Etapa */}
                  <text
                    x={xLeftMid + innerPad + 24}
                    y={centerY + 5}
                    fill={isLight ? "#0F172A" : "#FFFFFF"}
                    fontSize="14"
                    fontWeight="700"
                    fontFamily="sans-serif"
                    stroke="none"
                    style={{ stroke: "none", pointerEvents: "none", userSelect: "none" }}
                  >
                    {stage.name}
                  </text>

                  {/* Métricas: Conteo de Leads y Monto USD */}
                  <text
                    x={svgWidth / 2}
                    y={centerY + 5}
                    fill={isLight ? "#0F172A" : "#FFFFFF"}
                    fontSize="14"
                    fontWeight="800"
                    textAnchor="middle"
                    fontFamily="sans-serif"
                    stroke="none"
                    style={{ stroke: "none", pointerEvents: "none", userSelect: "none" }}
                  >
                    <tspan fill={isLight ? "#0F172A" : "#FFFFFF"}>{stage.count} {i18n.t("reports.funnel.leadsSuffix", "leads")}</tspan>
                    <tspan fill={isLight ? "rgba(0, 0, 0, 0.25)" : "rgba(255, 255, 255, 0.35)"} dx="12">|</tspan>
                    <tspan fill={isLight ? "#15803D" : "#4CAF50"} fontWeight="700" dx="12">
                      {currency} {stage.totalValue.toFixed(2)}
                    </tspan>
                  </text>

                  {/* Insignia de Lead Time Promedio con margen interior seguro */}
                  <g style={{ pointerEvents: "none" }}>
                    <rect
                      x={badgeX}
                      y={badgeY}
                      width={badgeWidth}
                      height={badgeHeight}
                      rx={13}
                      fill={isLight ? "rgba(245, 158, 11, 0.12)" : "rgba(255, 179, 0, 0.12)"}
                      stroke={isLight ? "rgba(217, 119, 6, 0.4)" : "rgba(255, 179, 0, 0.4)"}
                      strokeWidth="1"
                      style={{ stroke: isLight ? "rgba(217, 119, 6, 0.4)" : "rgba(255, 179, 0, 0.4)", strokeWidth: 1 }}
                    />
                    <circle
                      cx={badgeX + 15}
                      cy={centerY}
                      r={5.5}
                      fill="none"
                      stroke={isLight ? "#B45309" : "#FFB300"}
                      strokeWidth="1.4"
                      style={{ stroke: isLight ? "#B45309" : "#FFB300", strokeWidth: 1.4 }}
                    />
                    <path
                      d={`M ${badgeX + 15} ${centerY - 3} L ${badgeX + 15} ${centerY} L ${badgeX + 17.5} ${centerY + 1}`}
                      fill="none"
                      stroke={isLight ? "#B45309" : "#FFB300"}
                      strokeWidth="1.4"
                      style={{ stroke: isLight ? "#B45309" : "#FFB300", strokeWidth: 1.4 }}
                    />
                    <text
                      x={badgeX + 27}
                      y={centerY + 4}
                      fill={isLight ? "#92400E" : "#FFB300"}
                      fontSize="11.5"
                      fontWeight="700"
                      fontFamily="sans-serif"
                      stroke="none"
                      style={{ stroke: "none", userSelect: "none" }}
                    >
                      {i18n.t("reports.funnel.leadTimeLabel", "Lead Time:")} {stage.avgLeadTimeFormatted}
                    </text>
                  </g>

                  {/* Conector y Tasa de Paso hacia la etapa siguiente */}
                  {idx < numStages - 1 && (
                    <g style={{ pointerEvents: "none" }}>
                      <rect
                        x={svgWidth / 2 - 45}
                        y={y1 + 1}
                        width={90}
                        height={14}
                        rx={7}
                        fill={`${theme.palette.primary.main}26`}
                        stroke={`${theme.palette.primary.main}4D`}
                        strokeWidth="1"
                        style={{ stroke: `${theme.palette.primary.main}4D`, strokeWidth: 1 }}
                      />
                      <text
                        x={svgWidth / 2}
                        y={y1 + 11.5}
                        fill={theme.palette.primary.main}
                        fontSize="10"
                        fontWeight="700"
                        textAnchor="middle"
                        fontFamily="sans-serif"
                        stroke="none"
                        style={{ stroke: "none", userSelect: "none" }}
                      >
                        ➔ {stages[idx + 1]?.conversionRate || 0}%
                      </text>
                    </g>
                  )}
                </g>
              );
            })}
          </svg>
        </div>
      )}

      {/* 2. MODO: PIPELINE HORIZONTAL / TARJETAS DETALLADAS */}
      {viewMode === "pipeline" && (
        <div className={classes.funnelGrid}>
          {stages.map((stage, index) => {
            const isSelected = String(selectedLane) === String(stage.id);
            const isLast = index === stages.length - 1;

            return (
              <React.Fragment key={stage.id}>
                {/* Tarjeta de la Etapa */}
                <Tooltip
                  title={
                    isSelected
                      ? i18n.t("reports.funnel.clickToClear", "Clic para deseleccionar filtro")
                      : i18n.t("reports.funnel.clickToFilter", "Clic para filtrar atenciones por esta etapa")
                  }
                  arrow
                >
                  <div
                    className={`${classes.stageCard} ${isSelected ? classes.activeStageCard : ""}`}
                    onClick={() => onSelectStage(stage.id)}
                  >
                    <div>
                      {/* Barra de color de la etapa */}
                      <div
                        className={classes.stageTopBar}
                        style={{ backgroundColor: stage.color || "#00E5FF" }}
                      />

                      {/* Nombre de la etapa */}
                      <Typography className={classes.stageTitle} title={stage.name}>
                        {stage.name}
                      </Typography>

                      {/* Conteo de tickets */}
                      <Box display="flex" alignItems="baseline">
                        <span className={classes.countText}>{stage.count}</span>
                        <span className={classes.leadsLabel}>
                          {i18n.t("reports.funnel.leadsSuffix", "leads")}
                        </span>
                      </Box>

                      {/* Monto financiero de oportunidad */}
                      <div className={classes.valueText}>
                        <AttachMoney style={{ fontSize: 16 }} />
                        <span>{currency} {stage.totalValue.toFixed(2)}</span>
                      </div>
                    </div>

                    {/* Insignia de Lead Time Promedio */}
                    <div className={classes.leadTimeBadge}>
                      <AccessTime className={classes.leadTimeIcon} />
                      <span>
                        {i18n.t("reports.funnel.leadTimeLabel", "Lead Time:")} {stage.avgLeadTimeFormatted}
                      </span>
                    </div>
                  </div>
                </Tooltip>

                {/* Conector inter-etapas (Conversión y Pérdida) */}
                {!isLast && (
                  <div className={classes.connectorWrapper}>
                    <Tooltip
                      title={`${i18n.t("reports.funnel.conversionTooltip", "Tasa de paso hacia")} ${stages[index + 1]?.name}: ${stages[index + 1]?.conversionRate}%`}
                      arrow
                    >
                      <Chip
                        size="small"
                        label={`${stages[index + 1]?.conversionRate || 0}%`}
                        className={classes.connectorChip}
                      />
                    </Tooltip>
                    <ArrowForward className={classes.connectorArrow} />
                    {stages[index + 1]?.dropoffRate > 0 && (
                      <Tooltip
                        title={`${i18n.t("reports.funnel.dropoffTooltip", "Tasa de fuga / abandono:")} ${stages[index + 1]?.dropoffRate}%`}
                        arrow
                      >
                        <span className={classes.dropoffLabel}>
                          -{stages[index + 1]?.dropoffRate}%
                        </span>
                      </Tooltip>
                    )}
                  </div>
                )}
              </React.Fragment>
            );
          })}
        </div>
      )}
    </Paper>
  );
};

export default FunnelChart;
